import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  MessageSquare, 
  Mail, 
  FileText, 
  Receipt, 
  Users, 
  Search, 
  FolderKanban, 
  Brain, 
  AlertTriangle,
  X,
  Check,
  Bot,
  Zap,
  Building2,
  LayoutDashboard,
  Clock,
  MessageCircle,
  Sparkles,
  ArrowRight,
  Heart
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const beforeItems = [
  { icon: Phone, text: "Téléphone qui sonne toute la journée" },
  { icon: MessageSquare, text: "Messages WhatsApp non traités" },
  { icon: Mail, text: "Emails en retard" },
  { icon: FileText, text: "Devis envoyés tard le soir" },
  { icon: Receipt, text: "Factures oubliées, paiements en retard" },
  { icon: AlertTriangle, text: "Trésorerie tendue faute de relances" },
  { icon: Users, text: "Clients qui attendent des réponses" },
  { icon: Search, text: "Prospection jamais faite" },
  { icon: FolderKanban, text: "Organisation des chantiers compliquée" },
  { icon: Brain, text: "Stress permanent, charge mentale énorme" },
];

const afterItems = [
  { icon: Users, text: "Un assistant à mi-temps ou plein temps pour ton activité" },
  { icon: Bot, text: "Des agents IA qui travaillent 24h/24" },
  { icon: Building2, text: "Un CRM bâtiment prêt à l'emploi" },
  { icon: FileText, text: "Devis et factures automatisés" },
  { icon: Receipt, text: "Relances de paiement automatiques" },
  { icon: LayoutDashboard, text: "Trésorerie sous contrôle" },
  { icon: FolderKanban, text: "Pipeline chantier structuré" },
  { icon: Clock, text: "Suivi des chantiers et du temps passé" },
  { icon: MessageCircle, text: "Réponses automatiques aux clients" },
  { icon: Heart, text: "Du temps pour toi et ta famille" },
];

export default function TransformationSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section 
      ref={ref}
      className="py-16 md:py-24 bg-slate-50 overflow-hidden"
      data-testid="transformation-section"
    >
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-12 md:mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-white text-slate-600 text-sm font-medium rounded-full mb-4 shadow-sm">
            <Sparkles className="w-4 h-4 text-ba-secondary" />
            Transformation entrepreneur
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 mb-4">
            Transforme tes obstacles en tremplin
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Découvre ce qui change quand tu passes de l'artisan débordé à l'entrepreneur organisé.
          </p>
        </motion.div>

        {/* Before/After Grid */}
        <div className="grid lg:grid-cols-2 gap-6 md:gap-8 mb-12">
          
          {/* BEFORE Column */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="bg-gradient-to-br from-red-50 via-orange-50 to-amber-50 rounded-3xl p-6 md:p-8 border border-red-100 h-full">
              {/* Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center">
                  <X className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-bold text-slate-800">Avant BatiAssist</h3>
                  <p className="text-red-500 font-medium">L'entrepreneur débordé</p>
                </div>
              </div>

              {/* Items List */}
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                className="space-y-3"
              >
                {beforeItems.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={index}
                      variants={itemVariants}
                      className="flex items-center gap-3 p-3 bg-white/60 rounded-xl border border-red-100/50 hover:bg-white transition-colors group"
                    >
                      <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-red-200 transition-colors">
                        <Icon className="w-4 h-4 text-red-500" />
                      </div>
                      <span className="text-slate-700 text-sm md:text-base">{item.text}</span>
                    </motion.div>
                  );
                })}
              </motion.div>

              {/* Conclusion */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : {}}
                transition={{ delay: 0.8 }}
                className="mt-6 p-4 bg-red-100/50 rounded-xl border border-red-200"
              >
                <p className="text-red-700 font-medium text-center">
                  Tu es artisan… mais ton entreprise repose entièrement sur toi.
                </p>
              </motion.div>
            </div>
          </motion.div>

          {/* AFTER Column */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 rounded-3xl p-6 md:p-8 border border-green-100 h-full">
              {/* Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-bold text-slate-800">Après BatiAssist</h3>
                  <p className="text-green-600 font-medium">L'entreprise organisée</p>
                </div>
              </div>

              {/* Items List */}
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                className="space-y-3"
              >
                {afterItems.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={index}
                      variants={itemVariants}
                      className="flex items-center gap-3 p-3 bg-white/60 rounded-xl border border-green-100/50 hover:bg-white transition-colors group"
                    >
                      <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-green-200 transition-colors">
                        <Icon className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-slate-700 text-sm md:text-base">{item.text}</span>
                    </motion.div>
                  );
                })}
              </motion.div>

              {/* Conclusion */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : {}}
                transition={{ delay: 0.8 }}
                className="mt-6 p-4 bg-green-100/50 rounded-xl border border-green-200"
              >
                <p className="text-green-700 font-medium text-center">
                  Tu peux enfin te concentrer sur ton métier.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Central Message */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="inline-block bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-slate-100 max-w-3xl">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="w-12 h-1 bg-gradient-to-r from-red-400 to-red-300 rounded-full"></div>
              <Sparkles className="w-8 h-8 text-ba-secondary" />
              <div className="w-12 h-1 bg-gradient-to-r from-green-300 to-green-400 rounded-full"></div>
            </div>
            <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-slate-800 mb-4">
              BatiAssist transforme ton quotidien d'artisan débordé{' '}
              <span className="text-ba-secondary">en entreprise organisée.</span>
            </h3>
            <p className="text-slate-600 mb-6">
              Rejoins les entrepreneurs du bâtiment qui ont repris le contrôle de leur activité.
            </p>
            <Link to="/nos-solutions">
              <Button className="bg-ba-secondary hover:bg-ba-secondary/90 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all group">
                Découvrir toutes les solutions BatiAssist
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
